import { useCallback, useMemo, useState, type FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCreateTrip } from './useTrips'
import { Button } from '../../shared/components/Button'
import { GeoapifyMap, type MapMarker } from '../../shared/components/GoogleMap'
import { searchPlaces, type PlaceSearchResult } from '../../shared/api/placeSearch'

export function CreateTripManual() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const createTrip = useCreateTrip()
  const [title, setTitle] = useState('')
  const [destination, setDestination] = useState('')
  const [geography, setGeography] = useState<Array<{name:string;lat:number;lng:number}>>([])
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<PlaceSearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [mapName, setMapName] = useState('')
  const [zone, setZone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Etc/UTC')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [error, setError] = useState<string | null>(null)
  const markers = useMemo<MapMarker[]>(() => geography.map((g,i) => ({id:`region-${i}`,lat:g.lat,lng:g.lng,title:g.name})),[geography])
  const mapClick = useCallback((lat:number,lng:number) => {
    if (!mapName.trim()) {setError(t('manual.pin_name_required'));return}
    setGeography(current => [...current,{name:mapName.trim(),lat,lng}]);setMapName('');setError(null)
  }, [mapName,t])
  async function findPlace() {setSearching(true);setError(null)
    try {setResults(await searchPlaces(search,i18n.language))}
    catch(err){setError(err instanceof Error?err.message:String(err))}
    finally{setSearching(false)}}

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!title.trim()) {
      setError(t('manual.name_required'))
      return
    }
    if (startDate && endDate && endDate < startDate) {
      setError(t('manual.dates_invalid'))
      return
    }
    try { const trip = await createTrip.mutateAsync({
      title: title.trim(),
      destination: geography.length ? geography.map(g=>g.name).join(', ') : destination.trim(),
      geography,
      time_zone_id: zone,
      start_date: startDate || null,
      end_date: endDate || null,
    })
    navigate(`/trips/${trip.id}/places`)
    } catch(err) {setError(err instanceof Error?err.message:String(err))}
  }

  return (
    <div className="mx-auto max-w-xl">
      <Link
        to="/trips/new"
        className="mb-4 inline-flex items-center gap-1 text-sm text-st-muted hover:text-st-text"
      >
        <ArrowLeft className="h-4 w-4" /> {t('manual.back')}
      </Link>

      <h1 className="font-display text-2xl font-bold text-st-text">{t('manual.title')}</h1>
      <p className="mt-1 text-sm text-st-muted">{t('manual.intro')}</p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <Field label={t('manual.trip_name')} required>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('manual.trip_name_hint')}
            className="input"
            autoFocus
          />
        </Field>

        <Field label={t('manual.geography')}>
          <input
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder={t('manual.geography_hint')}
            className="input"
          />
        </Field>

        <div className="rounded-st-lg border border-st-line bg-st-surface p-3">
          <div className="flex gap-2"><input className="input min-w-0 flex-1" value={search} onChange={e=>setSearch(e.target.value)}
            placeholder={t('manual.search')} aria-label={t('manual.search')} />
            <Button type="button" variant="outline" disabled={searching||search.trim().length<2} onClick={()=>void findPlace()}>{t('manual.find')}</Button></div>
          {results.length>0&&<ul className="mt-2 max-h-40 overflow-y-auto">{results.map((r,i)=><li key={`${r.provider_id}-${i}`}>
            <button type="button" className="w-full p-1 text-start text-sm hover:bg-st-surface-2" onClick={()=>{
              setGeography([...geography,{name:r.title,lat:r.lat,lng:r.lng}]);setResults([]);setSearch('')
            }}>{r.title} · {r.address}</button></li>)}</ul>}
          {geography.length>0&&<><div className="mt-2 flex flex-wrap gap-2">{geography.map((g,i)=><button key={i} type="button"
            className="rounded-full bg-st-primary/10 px-2 py-1 text-xs text-st-primary" onClick={()=>setGeography(geography.filter((_,n)=>n!==i))}>
            {g.name} ×</button>)}</div>
            <label className="mt-2 block text-xs">{t('manual.pin_name')}<input className="input" value={mapName} onChange={e=>setMapName(e.target.value)} /></label>
            <p className="mb-1 text-xs text-st-muted">{t('manual.pin_hint')}</p>
            <GeoapifyMap markers={markers} height="15rem" onMapClick={mapClick} /></>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label={t('manual.start')}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input"
            />
          </Field>
          <Field label={t('manual.end')}>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input"
            />
          </Field>
        </div>

        <Field label={t('manual.zone')}><input className="input" value={zone} onChange={e=>setZone(e.target.value)} /></Field>

        {error && <p className="text-sm text-st-danger">{error}</p>}

        <div className="flex gap-3 pt-2">
          <Button type="submit" variant="accent" disabled={createTrip.isPending}>
            {createTrip.isPending ? t('manual.creating') : t('manual.create')}
          </Button>
          <Link to="/trips/new">
            <Button type="button" variant="ghost">
              {t('manual.cancel')}
            </Button>
          </Link>
        </div>
      </form>

      <style>{`
        .input {
          width: 100%;
          min-height: 2.75rem;
          border-radius: var(--radius-st-md);
          border: 1px solid var(--color-st-line);
          background: var(--color-st-surface);
          padding: 0 0.875rem;
          color: var(--color-st-text);
          font-size: 0.95rem;
        }
        .input:focus { outline: 2px solid var(--color-st-info); outline-offset: 1px; }
      `}</style>
    </div>
  )
}

function Field({
  label,
  required,
  children,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-st-text">
        {label}
        {required && <span className="text-st-danger"> *</span>}
      </span>
      {children}
    </label>
  )
}
